import React, { useState, useCallback, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { ImageOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface SafeImageProps {
    src?: string;
    alt: string;
    style?: React.CSSProperties;
    loading?: 'lazy' | 'eager';
    onLoad?: () => void;
    onError?: () => void;
    text?: string;
    fallbackComponent?: React.ReactNode;
}

const SafeImage: React.FC<SafeImageProps> = ({
    src,
    alt,
    style,
    loading = 'lazy',
    onLoad,
    onError,
    text = '',
    fallbackComponent
}) => {
    const { t } = useTranslation();
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [retryCount, setRetryCount] = useState(0);
    const imgRef = useRef<HTMLImageElement>(null);

    const handleImageLoad = useCallback(() => {
        setIsLoading(false);
        setHasError(false);
        onLoad?.();
    }, [onLoad]);

    const handleImageError = useCallback(() => {
        if (retryCount < 2) {
            // Retry loading the image up to 2 times
            setRetryCount((prev) => prev + 1);
            setTimeout(() => {
                if (imgRef.current) {
                    // Force reload by changing src
                    const srcWithCacheBust = src ? `${src}${src.includes('?') ? '&' : '?'}retry=${Date.now()}` : '';
                    imgRef.current.src = srcWithCacheBust;
                }
            }, 500);
        } else {
            setHasError(true);
            setIsLoading(false);
            onError?.();
        }
    }, [onError, retryCount, src]);

    // Detect dark mode for fallback background
    const isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const defaultFallback = (
        <Box
            sx={{
                ...style,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDark ? 'rgba(40,40,40,0.7)' : 'rgba(128, 128, 128, 0.1)',
                border: '2px dashed rgba(128, 128, 128, 0.3)',
                borderRadius: 2,
                color: 'text.secondary',
                padding: 4,
                minHeight: style?.minHeight,
                width: '100%',
                height: '100%',
                position: 'relative',
                maxWidth: style?.maxWidth || '100%',
                maxHeight: style?.maxHeight || '100%'
            }}
        >
            <ImageOutlined sx={{ fontSize: 64, opacity: 0.5 }} />
            <Typography
                variant="h6"
                color="text.secondary"
                textAlign="center"
                className="elegant-italic"
                sx={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic', fontSize: '1.5rem' }}
            >
                {text || t('image_unavailable', '')}
            </Typography>
            <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
                sx={{
                    mt: 1,
                    opacity: 0.7,
                    fontFamily: '"Cormorant Garamond", serif',
                    fontStyle: 'italic'
                }}
            >
                {text || t('unable_to_load_image', '')}
            </Typography>
        </Box>
    );

    if (hasError || !src) {
        return fallbackComponent || defaultFallback;
    }

    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%', ...style }}>
            {isLoading && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: isDark ? 'rgba(40,40,40,0.7)' : 'rgba(128, 128, 128, 0.1)',
                        zIndex: 1,
                    }}
                >
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        className="elegant-italic"
                        sx={{
                            fontFamily: '"Cormorant Garamond", serif',
                            fontStyle: 'italic',
                            fontSize: '1.2rem'
                        }}
                    >
                        {text || t('loading', '')}
                    </Typography>
                </Box>
            )}
            <img
                ref={imgRef}
                src={src}
                alt={alt}
                style={{
                    ...style,
                    opacity: isLoading ? 0 : 1,
                    transition: 'all 0.3s ease',
                }}
                loading={loading}
                onLoad={handleImageLoad}
                onError={handleImageError}
                draggable={false}
            />
        </Box>
    );
};

export default SafeImage;